/**
 * ProductColumnPicker - Dropdown to toggle column visibility.
 *
 * Uses DropdownMenuCheckboxItem for each toggleable column.
 */

import { Settings2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Table, VisibilityState } from '@tanstack/react-table'
import { Button } from '@/components/v2/ui/Button'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/v2/ui/DropdownMenu'
import type { Product } from '@/types/products'

const EXCLUDED = new Set(['actions', 'select'])

interface ProductColumnPickerProps {
  table: Table<Product>
  onVisibilityChange: (v: VisibilityState) => void
}

export function ProductColumnPicker({ table, onVisibilityChange }: ProductColumnPickerProps) {
  const { t } = useTranslation()

  const COLUMN_LABELS: Record<string, string> = {
    image: t('products.col_image'),
    name: t('products.col_name'),
    sku: t('products.col_sku'),
    gtin: t('products.col_gtin'),
    price_net: t('products.col_price_net'),
    is_active: t('products.col_status'),
    quantity_30d: t('products.col_quantity_30d'),
    revenue_30d: t('products.col_revenue_30d'),
    quantity_90d: t('products.col_quantity_90d'),
    revenue_90d: t('products.col_revenue_90d'),
    readiness_action: t('products.col_readiness'),
    packshot: t('products.col_packshot'),
    readiness_score: t('products.col_score'),
    issues_count: t('products.col_issues'),
  }

  const columns = table.getAllColumns().filter((c) => !EXCLUDED.has(c.id))

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" iconLeft={<Settings2 className="h-4 w-4" />}>
          {t('products.col_picker_label')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>{t('products.col_picker_visible')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {columns.map((column) => (
          <DropdownMenuCheckboxItem
            key={column.id}
            checked={column.getIsVisible()}
            onCheckedChange={(checked) => {
              const next = { ...table.getState().columnVisibility, [column.id]: !!checked }
              table.setColumnVisibility(next)
              onVisibilityChange(next)
            }}
          >
            {COLUMN_LABELS[column.id] ?? column.id}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
